import { useState } from "react";
import {
  useListResourcePricing,
  useUpdateResourcePricing,
  getListResourcePricingQueryKey,
  useListInventoryPlans,
} from "@workspace/api-client-react";
import type { ResourcePricing, ResourcePricingCadence, ResourcePricingStatus } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CircleDollarSign,
  Filter,
  Pencil,
  Archive,
  ArchiveRestore,
  Plus,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { EditPricingDialog } from "@/components/settings/edit-pricing-dialog";
import { toast } from "sonner";

// Phase 10 (Plan 10-07): SUBTYPES constant removed — replaced with planId picker driven by canonical 9 plans.

const CADENCES = [
  { value: "Monthly", label: "Monthly" },
  { value: "Quarterly", label: "Quarterly" },
  { value: "Six_Month", label: "Six Month" },
  { value: "Hourly", label: "Hourly" },
  { value: "Daily", label: "Daily" },
  { value: "Booking", label: "Booking" },
] as const;

function cadenceLabel(c: string): string {
  return CADENCES.find((cd) => cd.value === c)?.label ?? c;
}

function StatusBadge({ status }: { status: string }) {
  if (status === "Active") {
    return (
      <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-100">
        Active
      </Badge>
    );
  }
  return (
    <Badge className="bg-zinc-100 text-zinc-600 border-zinc-200 hover:bg-zinc-100">
      Archived
    </Badge>
  );
}

export default function ResourcePricingPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<"All" | "Active" | "Archived">("All");
  // Phase 10 (Plan 10-07): planId filter replaces subtype filter.
  const [planFilter, setPlanFilter] = useState<string>("all");
  const [cadenceFilter, setCadenceFilter] = useState<string>("all");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState<ResourcePricing | null>(null);
  const [archiveRow, setArchiveRow] = useState<ResourcePricing | null>(null);

  const { data: plans = [] } = useListInventoryPlans({});
  const planLabel = (id: number) => plans.find((p) => p.id === id)?.planName ?? `Plan #${id}`;

  const params: {
    status?: ResourcePricingStatus;
    planId?: number;
    cadence?: ResourcePricingCadence;
  } = {
    ...(statusFilter !== "All" ? { status: statusFilter as ResourcePricingStatus } : {}),
    ...(planFilter !== "all" ? { planId: Number(planFilter) } : {}),
    ...(cadenceFilter !== "all" ? { cadence: cadenceFilter as ResourcePricingCadence } : {}),
  };

  const { data: rows, isLoading } = useListResourcePricing(params, {
    query: { queryKey: getListResourcePricingQueryKey(params) },
  });

  const updatePricing = useUpdateResourcePricing();

  function openAdd() {
    setEditRow(null);
    setEditDialogOpen(true);
  }

  function openEdit(row: ResourcePricing) {
    setEditRow(row);
    setEditDialogOpen(true);
  }

  async function handleArchive() {
    if (!archiveRow) return;
    try {
      await updatePricing.mutateAsync({
        id: archiveRow.id,
        data: { status: "Archived" },
      });
      queryClient.invalidateQueries({ queryKey: getListResourcePricingQueryKey() });
      toast.success("Pricing row archived.");
    } catch {
      toast.error("Failed to archive pricing row.");
    } finally {
      setArchiveRow(null);
    }
  }

  async function handleRestore(row: ResourcePricing) {
    try {
      await updatePricing.mutateAsync({
        id: row.id,
        data: { status: "Active" },
      });
      queryClient.invalidateQueries({ queryKey: getListResourcePricingQueryKey() });
      toast.success("Pricing row restored.");
    } catch {
      toast.error("Failed to restore pricing row.");
    }
  }

  const isEmpty = !isLoading && (!rows || rows.length === 0);
  const hasFilter = planFilter !== "all" || cadenceFilter !== "all" || statusFilter !== "All";

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Resource Pricing</h1>
          <p className="text-sm text-muted-foreground">
            Per-subtype per-cadence pricing used to calculate booking charges and recurring invoices.
          </p>
        </div>
        <Button onClick={openAdd}>
          <Plus className="mr-2 h-4 w-4" />
          Add Pricing Row
        </Button>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Status chips */}
        {(["All", "Active", "Archived"] as const).map((s) => (
          <Button
            key={s}
            variant={statusFilter === s ? "secondary" : "outline"}
            size="sm"
            onClick={() => setStatusFilter(s)}
          >
            {s}
          </Button>
        ))}

        {/* Plan Select (Phase 10 — replaces Subtype filter) */}
        <Select value={planFilter} onValueChange={setPlanFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All plans" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plans</SelectItem>
            {plans.map((p) => (
              <SelectItem key={p.id} value={String(p.id)}>{p.planName}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Cadence Select */}
        <Select value={cadenceFilter} onValueChange={setCadenceFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All cadences" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All cadences</SelectItem>
            {CADENCES.map((cd) => (
              <SelectItem key={cd.value} value={cd.value}>{cd.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isEmpty ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          {hasFilter ? (
            <>
              <Filter className="h-8 w-8 text-muted-foreground mb-2" />
              <h3 className="text-base font-medium">No pricing rows match this filter</h3>
              <p className="text-sm text-muted-foreground">Try adjusting the plan or cadence filter.</p>
            </>
          ) : (
            <>
              <CircleDollarSign className="h-8 w-8 text-muted-foreground mb-2" />
              <h3 className="text-base font-medium">No resource pricing configured</h3>
              <p className="text-sm text-muted-foreground">
                Add your first pricing row to enable booking charges and recurring invoice generation.
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead style={{ width: 140 }}>Plan</TableHead>
                <TableHead style={{ width: 100 }}>Cadence</TableHead>
                <TableHead style={{ width: 100 }}>Payment Unit</TableHead>
                <TableHead style={{ width: 96 }} className="text-right">Base Price</TableHead>
                <TableHead style={{ width: 120 }}>Security Deposit</TableHead>
                <TableHead style={{ width: 80 }} className="text-right">Discount %</TableHead>
                <TableHead style={{ width: 88 }} className="text-right">Lock-in (mo)</TableHead>
                <TableHead style={{ width: 88 }}>Status</TableHead>
                <TableHead style={{ width: 96 }}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 9 }).map((_, j) => (
                      <TableCell key={j}>
                        <div className="h-4 w-full animate-pulse rounded bg-muted" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                (rows ?? []).map((row) => (
                  <TableRow key={row.id} className={cn(row.status === "Archived" && "opacity-60")}>
                    <TableCell className="py-2">
                      <span className="text-sm">{planLabel(row.planId)}</span>
                    </TableCell>
                    <TableCell className="py-2 text-sm">{cadenceLabel(row.cadence)}</TableCell>
                    <TableCell className="py-2 text-sm">{row.paymentUnit}</TableCell>
                    <TableCell className="py-2 text-right font-mono text-sm">
                      ₹{Number(row.basePrice).toFixed(2)}
                    </TableCell>
                    <TableCell className="py-2 text-sm">
                      {row.securityDepositRequired && row.securityDepositAmount
                        ? <span className="font-mono">₹{Number(row.securityDepositAmount).toFixed(2)}</span>
                        : <span className="text-muted-foreground">None</span>}
                    </TableCell>
                    <TableCell className="py-2 text-right text-sm">
                      {row.discountPct ? `${Number(row.discountPct)}%` : "—"}
                    </TableCell>
                    <TableCell className="py-2 text-right text-sm">
                      {row.lockInMonths ?? "—"}
                    </TableCell>
                    <TableCell className="py-2">
                      <StatusBadge status={row.status} />
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Edit pricing row"
                          onClick={() => openEdit(row)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {row.status === "Active" ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Archive pricing row"
                            onClick={() => setArchiveRow(row)}
                          >
                            <Archive className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Restore pricing row"
                            onClick={() => handleRestore(row)}
                          >
                            <ArchiveRestore className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit / Add Dialog */}
      <EditPricingDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        row={editRow}
      />

      {/* Archive Confirm AlertDialog */}
      <AlertDialog open={archiveRow !== null} onOpenChange={(o) => { if (!o) setArchiveRow(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Archive {archiveRow ? `${planLabel(archiveRow.planId)} / ${cadenceLabel(archiveRow.cadence)}` : ""} pricing?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Archived rows remain in historical charge records. New bookings will not use this row.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive} disabled={updatePricing.isPending}>
              Archive Pricing Row
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
